/**
 * Styles for the Template Library tab and detail page, traced from the
 * Template Library design. Colours come from `theme/colors`; the design's
 * orange maps to `signer.orange`, which tracks the app brand colour.
 *
 * The search field reuses `Templates.styles` so both tabs share one control.
 */
import { alpha } from "@mui/material/styles";
import {
  button,
  contentCard,
  notification,
  signer,
  statusChip,
  white,
} from "theme/colors";

const ink = signer.ink;
const successTone = statusChip.tone.success;
/** Ink-tinted drop shadow, the way `theme/colors` builds its shadows. */
const inkShadow = (geometry: string, opacity: number) =>
  `${geometry} ${alpha(ink, opacity)}`;
const border = contentCard.border;
const brand = signer.orange;
const purple = signer.purple;

/** Small uppercase pill used for category / POPULAR / KIT / FROM LIBRARY. */
const pill = (color: string, bg: string) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "10.5px",
  fontWeight: 700,
  letterSpacing: ".04em",
  lineHeight: 1.4,
  color,
  bgcolor: bg,
  borderRadius: "999px",
  px: "9px",
  py: "3px",
  whiteSpace: "nowrap" as const,
});

const statLabel = {
  fontSize: "10.5px",
  fontWeight: 700,
  letterSpacing: ".08em",
  color: signer.text.faint,
};

const buttonBase = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "9px",
  width: "100%",
  borderRadius: "10px",
  fontFamily: "inherit",
  fontSize: "14px",
  fontWeight: 700,
  textTransform: "none" as const,
  boxShadow: "none",
};

const templateLibraryStyles = {
  // -- Tabs ---------------------------------------------------------------
  tabs: {
    display: "flex",
    gap: "28px",
    borderBottom: `1px solid ${border}`,
    mb: "22px",
  },
  tab: (active: boolean) => ({
    p: "10px 2px 12px",
    m: 0,
    mb: "-1px",
    border: 0,
    borderBottom: "2px solid",
    borderBottomColor: active ? brand.main : "transparent",
    background: "transparent",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 700,
    color: active ? brand.main : signer.text.subtitle,
    cursor: "pointer",
    "&:hover": { color: active ? brand.main : ink },
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  }),

  // -- Toolbar (search) ---------------------------------------------------
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    mb: "18px",
    flexWrap: { xs: "wrap", sm: "nowrap" },
  },

  // -- Sort ---------------------------------------------------------------
  sortGroup: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flexShrink: 0,
    ml: { xs: "auto", sm: 0 },
  },
  sortLabel: {
    fontSize: "13px",
    fontWeight: 600,
    color: signer.text.faint,
  },
  /** The design's closed dropdown: same height and border as the search field. */
  sortButton: {
    display: "flex",
    alignItems: "center",
    gap: "22px",
    bgcolor: white,
    border: `1px solid ${border}`,
    borderRadius: "12px",
    p: "12px 14px",
    fontFamily: "inherit",
    fontSize: "13.5px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: ink,
    cursor: "pointer",
    transition: "border-color .15s",
    "&:hover": { borderColor: signer.border.strong },
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  },
  sortChevron: { width: 13, height: 13, color: signer.text.meta },

  // -- Category chips -----------------------------------------------------
  chips: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    mb: "22px",
  },
  chip: (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    p: "8px 14px",
    borderRadius: "999px",
    border: "1px solid",
    borderColor: active ? ink : border,
    bgcolor: active ? ink : white,
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 700,
    color: active ? white : signer.text.secondary,
    cursor: "pointer",
    "&:hover": { borderColor: brand.main },
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  }),
  chipCount: (active: boolean) => ({
    fontSize: "11px",
    fontWeight: 700,
    color: active ? ink : signer.text.subtitle,
    bgcolor: active ? white : signer.surface.muted,
    borderRadius: "999px",
    p: "2px 7px",
    lineHeight: 1.3,
  }),
  resultCount: {
    ml: "auto",
    fontSize: "12.5px",
    color: signer.text.faint,
  },

  // -- Card grid ----------------------------------------------------------
  grid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0,1fr)",
      sm: "repeat(2,minmax(0,1fr))",
      md: "repeat(3,minmax(0,1fr))",
      lg: "repeat(4,minmax(0,1fr))",
    },
    gap: "18px",
  },
  card: {
    position: "relative",
    bgcolor: white,
    border: `1px solid ${border}`,
    borderRadius: "14px",
    boxShadow: contentCard.shadow,
    p: "14px",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    textAlign: "start",
    fontFamily: "inherit",
    transition: "border-color .15s, box-shadow .15s",
    "&:hover": {
      borderColor: brand.main,
      boxShadow: inkShadow("0 8px 22px", 0.08),
    },
    "&:hover .library-use": { bgcolor: brand.tint, borderColor: brand.border },
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  },
  thumb: (isKit: boolean) => ({
    position: "relative",
    bgcolor: isKit ? purple.chipBg : signer.surface.muted,
    borderRadius: "10px",
    height: 150,
    boxSizing: "border-box",
    overflow: "hidden",
    mb: "14px",
    p: isKit ? 0 : "16px 26px 0",
  }),
  /** One of the three fanned sheets on a kit thumbnail. */
  kitSheet: (inset: number, top: number, rotate: number, z: number) => ({
    position: "absolute",
    left: inset,
    right: inset,
    top,
    bottom: -10,
    bgcolor: white,
    borderRadius: "4px",
    boxShadow: inkShadow("0 2px 10px", 0.09),
    transform: `rotate(${rotate}deg)`,
    zIndex: z,
    p: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  }),
  kitBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 4,
    display: "flex",
    alignItems: "center",
    gap: "5px",
    bgcolor: purple.main,
    color: white,
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: ".04em",
    borderRadius: "999px",
    p: "4px 10px",
  },
  thumbSheet: {
    bgcolor: white,
    borderRadius: "4px 4px 0 0",
    boxShadow: inkShadow("0 2px 10px", 0.07),
    height: "100%",
    p: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  /** The real cover image (page 1), filling a sheet from the top. */
  thumbImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "top",
    borderRadius: "inherit",
  },
  thumbTitle: {
    fontSize: "7.5px",
    fontWeight: 800,
    letterSpacing: ".05em",
    color: signer.docText,
    textAlign: "center",
    mb: "4px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  thumbLine: (width: string, strong = false) => ({
    height: "3px",
    borderRadius: "2px",
    bgcolor: strong ? signer.border.strong : signer.border.main,
    width,
    flexShrink: 0,
  }),
  cardTags: { display: "flex", alignItems: "center", gap: "6px", mb: "10px" },
  categoryPill: pill(signer.text.meta, signer.surface.muted),
  popularPill: pill(purple.main, purple.chipBg),
  kitPill: pill(purple.main, purple.chipBg),
  fromLibraryPill: pill(brand.main, brand.tint),
  newPill: pill(successTone.text, successTone.bg),
  cardName: {
    fontSize: "14.5px",
    fontWeight: 700,
    color: ink,
    mb: "5px",
    textWrap: "pretty",
  },
  cardDesc: {
    fontSize: "12.5px",
    lineHeight: 1.55,
    color: signer.text.subtitle,
    mb: "14px",
    textWrap: "pretty",
  },
  cardFoot: {
    mt: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  cardMeta: { fontSize: "11.5px", color: signer.text.faint },
  cardUse: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${border}`,
    borderRadius: "9px",
    p: "7px 11px",
    fontSize: "12px",
    fontWeight: 700,
    color: brand.main,
    whiteSpace: "nowrap",
    transition: "background-color .15s, border-color .15s",
  },
  /** Sentinel under the grid; the next page loads when it scrolls into view. */
  loadMore: {
    display: "flex",
    justifyContent: "center",
    minHeight: 24,
    py: "18px",
  },
  // -- Detail page --------------------------------------------------------
  crumbs: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    color: signer.text.subtitle,
    mb: "18px",
    minWidth: 0,
  },
  crumbLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    p: 0,
    border: 0,
    background: "transparent",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { color: brand.main },
  },
  /** Same back arrow and separator sizes as the Sign Request detail trail. */
  crumbBack: { width: 15, height: 15 },
  crumbSeparator: {
    width: 13,
    height: 13,
    color: signer.border.strong,
    flexShrink: 0,
  },
  crumbCurrent: {
    color: ink,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0,1fr)",
      md: "minmax(0,1fr) 372px",
    },
    gap: "22px",
    alignItems: "start",
  },
  docPane: {
    bgcolor: signer.surface.muted,
    border: `1px solid ${border}`,
    borderRadius: "16px",
    overflow: "hidden",
    minWidth: 0,
  },
  /** One tab per document of a kit, above the preview. */
  kitTabs: {
    display: "flex",
    gap: "8px",
    p: "12px 14px",
    bgcolor: white,
    borderBottom: `1px solid ${border}`,
    overflowX: "auto",
  },
  kitTab: (active: boolean, included = true) => ({
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "9px",
    p: "8px 12px 8px 8px",
    borderRadius: "10px",
    border: "1px solid",
    borderColor: active ? brand.main : border,
    bgcolor: active ? brand.faint : white,
    opacity: included ? 1 : 0.45,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "start",
    "&:hover": { borderColor: brand.main },
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  }),
  kitTabNum: (active: boolean) => ({
    width: 22,
    height: 22,
    borderRadius: "7px",
    bgcolor: active ? brand.main : signer.surface.muted,
    color: active ? white : signer.text.meta,
    fontSize: "11px",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  kitTabName: (active: boolean) => ({
    fontSize: "12.5px",
    fontWeight: 700,
    color: active ? brand.main : ink,
    whiteSpace: "nowrap",
    lineHeight: 1.25,
  }),
  kitTabPages: {
    fontSize: "10.5px",
    color: signer.text.faint,
    lineHeight: 1.25,
  },
  // -- "Included in this kit" list (details panel) ------------------------
  kitSection: { mb: "20px" },
  kitListHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: "10px",
  },
  kitCount: { fontSize: "11.5px", fontWeight: 700, color: signer.text.meta },
  kitList: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${border}`,
    borderRadius: "12px",
    overflow: "hidden",
  },
  /** The design keeps every row white; the kit tabs above mark the open one. */
  kitRow: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    p: "10px 12px",
    borderBottom: `1px solid ${signer.surface.muted}`,
    bgcolor: white,
    "&:last-of-type": { borderBottom: 0 },
  },
  /** The design's square tick box; brand fill when the document is included. */
  checkbox: (checked: boolean, locked: boolean) => ({
    width: 18,
    height: 18,
    flexShrink: 0,
    p: 0,
    borderRadius: "5px",
    border: "1.5px solid",
    borderColor: checked ? brand.main : signer.border.strong,
    bgcolor: checked ? brand.main : white,
    color: white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: locked ? "not-allowed" : "pointer",
    opacity: locked && !checked ? 0.6 : 1,
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  }),
  /** Name + meta; clicking it opens the document in the preview. */
  kitRowBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.3,
    m: 0,
    p: 0,
    border: 0,
    background: "transparent",
    fontFamily: "inherit",
    textAlign: "start",
    cursor: "pointer",
    "&:focus-visible": { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
  },
  kitRowName: (included: boolean) => ({
    fontSize: "12.5px",
    fontWeight: 700,
    color: included ? ink : signer.text.faint,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  kitRowMeta: { fontSize: "11px", color: signer.text.faint },
  kitRowNum: { fontSize: "10.5px", fontWeight: 700, color: signer.text.faint },
  kitNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    mt: "10px",
    fontSize: "11.5px",
    lineHeight: 1.55,
    color: signer.text.subtitle,
  },
  fileBar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    p: "12px 16px",
    bgcolor: white,
    borderBottom: `1px solid ${border}`,
  },
  fileName: {
    fontSize: "13px",
    fontWeight: 700,
    color: ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  filePages: {
    ml: "auto",
    fontSize: "12px",
    fontWeight: 600,
    color: signer.text.subtitle,
    whiteSpace: "nowrap",
  },
  zoomChip: {
    border: `1px solid ${border}`,
    borderRadius: "8px",
    p: "5px 9px",
    fontSize: "12px",
    fontWeight: 700,
    color: signer.text.secondary,
  },
  pages: {
    height: { xs: "70vh", md: "calc(100vh - 250px)" },
    minHeight: 520,
    overflow: "auto",
    p: "28px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "22px",
  },
  page: {
    width: "100%",
    maxWidth: 640,
    flexShrink: 0,
    bgcolor: white,
    boxShadow: inkShadow("0 4px 22px", 0.1),
    lineHeight: 0,
    "& canvas": { width: "100% !important", height: "auto !important" },
  },
  /** Positions the field boxes over a rendered page. */
  pageCanvas: { position: "relative", lineHeight: 0 },
  /**
   * One placed field on the preview, as in the design: dashed border and a
   * tint per signer role — brand for the first role, purple for the second.
   */
  fieldBox: (tone: 0 | 1 | 2) => ({
    position: "absolute",
    boxSizing: "border-box",
    border: "1.5px dashed",
    borderColor:
      tone === 0 ? brand.main : tone === 1 ? purple.main : signer.border.strong,
    borderRadius: "4px",
    bgcolor: tone === 0 ? brand.faint : tone === 1 ? purple.faint : white,
    color:
      tone === 0 ? brand.main : tone === 1 ? purple.main : signer.text.meta,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    pointerEvents: "none",
    lineHeight: 1.2,
    fontSize: "clamp(8px, 1.4vw, 11px)",
    fontWeight: 700,
    textAlign: "center",
    px: "4px",
  }),
  /** The caption inside a field box; ends in "…" when the box is narrow. */
  fieldCaption: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewImage: { display: "block", width: "100%", height: "auto" },
  previewUnavailable: { fontSize: "13px", color: signer.text.meta },
  pageLoading: {
    width: "100%",
    maxWidth: 640,
    aspectRatio: "612 / 792",
    bgcolor: white,
    boxShadow: inkShadow("0 4px 22px", 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  side: {
    position: { md: "sticky" },
    top: 0,
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },
  panel: {
    bgcolor: white,
    border: `1px solid ${border}`,
    borderRadius: "16px",
    boxShadow: contentCard.shadow,
    p: "20px 22px",
  },
  panelTags: { display: "flex", alignItems: "center", gap: "6px", mb: "14px" },
  title: {
    m: 0,
    mb: "8px",
    fontSize: "21px",
    fontWeight: 800,
    letterSpacing: "-.01em",
    lineHeight: 1.2,
    color: ink,
    textWrap: "pretty",
  },
  desc: {
    m: 0,
    mb: "18px",
    fontSize: "13.5px",
    lineHeight: 1.6,
    color: signer.text.meta,
  },
  stats: (columns: number) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${columns},1fr)`,
    gap: "12px",
    borderTop: `1px solid ${signer.surface.muted}`,
    borderBottom: `1px solid ${signer.surface.muted}`,
    py: "14px",
    mb: "20px",
  }),
  stat: { display: "flex", flexDirection: "column", gap: "3px" },
  statLabel,
  statValue: { fontSize: "13px", fontWeight: 700, color: ink },
  actions: { display: "flex", flexDirection: "column", gap: "10px" },
  primaryButton: {
    ...buttonBase,
    bgcolor: brand.main,
    color: white,
    height: 44,
    p: "0 18px",
    fontSize: "13.5px",
    "&:hover": { bgcolor: brand.dark, boxShadow: "none" },
    // These are `text` Buttons coloured through sx. The theme gives every text
    // Button a near-transparent `:active` background, which turned the brand
    // fill white while the mouse was held down; MUI's disabled rule greys the
    // label. Pin both to this button's own colours.
    "&:active": { bgcolor: brand.dark },
    "&.Mui-disabled": { bgcolor: brand.main, color: white, opacity: 0.7 },
  },
  secondaryButton: {
    ...buttonBase,
    bgcolor: white,
    color: signer.text.secondary,
    border: `1px solid ${button.secondaryBorderWarm}`,
    height: 44,
    p: "0 16px",
    fontSize: "13.5px",
    "&:hover": { bgcolor: white, borderColor: ink, color: ink },
    // Same reason as primaryButton.
    "&:active": { bgcolor: white, borderColor: ink, color: ink },
    "&.Mui-disabled": {
      bgcolor: white,
      color: signer.text.secondary,
      borderColor: button.secondaryBorderWarm,
      opacity: 0.7,
    },
  },
  actionNote: {
    fontSize: "11.5px",
    lineHeight: 1.55,
    color: signer.text.faint,
  },
  importedBanner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    bgcolor: successTone.bg,
    borderRadius: "10px",
    p: "12px 14px",
    fontSize: "13px",
    fontWeight: 700,
    color: successTone.text,
  },
  stepsTitle: { fontSize: "15px", fontWeight: 800, color: ink, mb: "16px" },
  steps: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    m: 0,
    p: 0,
    listStyle: "none",
  },
  step: { display: "flex", alignItems: "flex-start", gap: "12px" },
  stepNum: {
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: "50%",
    bgcolor: brand.tint,
    color: brand.main,
    fontSize: "11.5px",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepTitle: { fontSize: "13px", fontWeight: 700, color: ink },
  stepText: { fontSize: "12px", color: signer.text.subtitle },
  // Design card tints for a library-sourced item in My Templates.
  importedCardBg: brand.faintest,
  importedCardBorder: brand.border,
  // Icon colours used inline by the detail page.
  pdfIconColor: signer.gradient.coral,
  kitNoteIconColor: purple.main,
  importedIconColor: notification.success.icon,
} as const;

export default templateLibraryStyles;
